import React, { useEffect, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button,
  Input, Text,
  VStack,
  List, ListItem,
  useDisclosure,
  border,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

interface SelectorButtonProps {
  /** タイトル */
  title: string,
  /** 選択ボタンの色 */
  colorScheme: string,
  /** IDプロパティ名 */
  idName: string,
  /** 名称プロパティ名 */
  displayName: string,
  /** 名称プロパティ名がnullの場合に表示するサブ名称 */
  subDisplayName?: string,
  /** 一覧データ */
  listData: Record<string, any>[] | null,
  /** 前回選択項目 */
  prevSelectedItem: Record<string, any> | null | undefined,
  /** 確定ボタン押下処理 */
  handleSetSelection: (selectedItem: Record<string, any> | null | undefined) => void,
  /** データ取得処理 */
  handleFetchData: () => void,
}

/**
 * 一覧選択表示ボタン
 */
const SelectorButton: React.FC<SelectorButtonProps> = ({
  title, colorScheme, idName, displayName, subDisplayName, listData, prevSelectedItem, handleSetSelection, handleFetchData,
}) => {
  const { t } = useTranslation();
  /** 状態管理 */
  const { isOpen, onOpen, onClose } = useDisclosure();

  /** フィルター値 */
  const [filterValue, setFilterValue] = useState("");

  /** 選択項目 */
  const [selectedItem, setSelectedItem] = useState<Record<string, any> | null>();

  /** フィルター後のリストデータ */
  const filteredList = (listData ?? []).filter((record) => (record[displayName] ?? '').toLowerCase().includes(filterValue.toLowerCase()));

  useEffect(() => {
    if (isOpen) {
      setSelectedItem(prevSelectedItem);

      if (!listData) {
        // データ取得
        handleFetchData();
      }
    }
  }, [isOpen]);

  return (
    <>
      {/* 選択ボタン */}
      {/* minWidth: 日本語/英語で文言長が異なりボタン幅が変わると、二列/一列の切替タイミングが言語間でズレるため、両言語で同じ幅になるよう固定 */}
      <Button colorScheme={colorScheme} ml={4} minWidth="100px" onClick={onOpen}>{t('common.button.select')}</Button>
      {/* 非表示の間は出力しない */}
      {isOpen && (
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{title}</ModalHeader>
            <ModalBody>
              <VStack spacing={4}>

                <Input placeholder={t('common.placeholder.searchEllipsis')} value={filterValue} onChange={(e) => setFilterValue(e.target.value)} />

                {/* 選択済み設置工場の表示 */}
                <Text fontWeight="bold" color="teal.500" alignSelf="flex-start">
                  {t('common.label.selected')}: {
                    (selectedItem?.[displayName] || listData?.find(m => m?.[idName] === selectedItem?.[idName])?.[displayName]) ||
                    (selectedItem?.[subDisplayName || ""] || listData?.find(m => m?.[idName] === selectedItem?.[idName])?.[subDisplayName || ""])
                  }
                </Text>

                {/* 一覧表示 */}
                <List spacing={3} overflowY="auto" maxHeight="300px" w="100%">
                  {filteredList.map((record) => (
                    <ListItem
                      as="button"
                      display="block"
                      w="100%"
                      textAlign="left"
                      key={record[idName]}
                      p={2}
                      borderRadius="md"
                      bg={
                        selectedItem?.[idName] === record[idName]
                          ? "teal.100"
                          : "blackAlpha 100"
                      }
                      _focus={{ border: 'none 1px transparent' }}
                      _hover={{ bg: "blue.100" }}
                      onClick={() => setSelectedItem(record)}
                      cursor="pointer"
                    >
                      {record[displayName] || (subDisplayName ? record[subDisplayName ?? ""] : "")}
                    </ListItem>
                  ))}
                </List>
              </VStack>

            </ModalBody>
            <ModalFooter>
              <Button colorScheme="blue" mr={1} ml={1}
                isDisabled={!selectedItem}
                onClick={() => {
                  handleSetSelection(selectedItem);
                  onClose();
                }}>
                {t('common.button.confirmAction')}
              </Button>
              <Button colorScheme="gray" mr={1} ml={1}
                onClick={onClose}>
                {t('common.button.close')}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal >
      )}
    </>
  );
}

export default SelectorButton;